import { components } from "./apex-client-gen";
import {
  getItemWithExpiry,
  isCashSweepSymbol,
  isClosedOrClosingAccount,
  sum,
  type AccountType,
  type AssetLiability,
  type AllocationType,
  type DepositInfo,
  type WithdrawalInfo,
} from "@wavvest/shared-utils";

export type {
  AccountType,
  AllocationType,
  AssetLiability,
  DepositInfo,
  WithdrawalInfo,
} from "@wavvest/shared-utils";

export {
  ACCOUNT_TYPES,
  GOALS,
  ASSET_LIABILITY,
  accountTypeToGoalType,
  accountTypeToName,
  sum,
} from "@wavvest/shared-utils";
export type { GoalType } from "@wavvest/shared-utils";

export const isUserLoggedIn = (): boolean => {
  const token = getItemWithExpiry("access_token");
  const user = getItemWithExpiry("user");
  return !!(token && user);
};

export const clearAuthStorage = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("user");
};

export { navigateToLogin } from "@wavvest/shared-utils";

export type AccountDetail = components["schemas"]["Account"];

export type GetIraDistributionConstraintsResponse =
  components["schemas"]["DistributionConstraints"];
export type GetIraContributionConstraintsResponse =
  components["schemas"]["ContributionConstraints"];
export type HoldingPosition = components["schemas"]["Position"];
export type HistoricalPosition =
  components["schemas"]["ClosingPositionSnapshotRecord"];

export type ApexAccount = {
  id: string;
  name: string;
  type: AccountType;
  total_equity: number;
  asset_or_liability: AssetLiability;
  total_equity_date?: Date | null;
  holdings?: components["schemas"]["Position"][];
  allocation?: {
    kind: string;
    risk_score: number;
    time_horizon_years: number;
  } | null;
  transferList?: {
    deposits?: any[];
    withdrawals?: any[];
  };
  cash_available_for_withdraw?: string;
  ira_contribution_constraints?: components["schemas"]["ContributionConstraints"];
  ira_distribution_constraints?: components["schemas"]["DistributionConstraints"];
  account_detail?: components["schemas"]["Account"];
};

export type AccountInfo = {
  goal: AccountType;
  account_detail: AccountDetail;
  cash_available_for_withdraw: string;
  open_balance: string;
  total_equity: string;
  buying_power: string;
  ira_contribution_constraints?: GetIraContributionConstraintsResponse;
  ira_distribution_constraints?: GetIraDistributionConstraintsResponse;
  deposits?: DepositInfo[];
  withdrawals?: WithdrawalInfo[];
  holdings?: HoldingPosition[];
  portfolios?: Partial<Record<AllocationType, number>>;
  historical_positions?: HistoricalPosition[];
};

export type GetAccountsResponse =
  components["schemas"]["CreateOrGetAccountsResponse"];

export type AssetMap = components["schemas"]["GetAssetMap"];

export type AdvisorArchAssetClass =
  components["schemas"]["AdvisorArchAssetClass"];

export type CreateAccountsResponse =
  components["schemas"]["CreateOrGetAccountsResponse"];

export function accountInfoFromApi(
  data: CreateAccountsResponse["accounts"][number],
): AccountInfo {
  let portfolios: Partial<Record<AllocationType, number>> | undefined =
    undefined;
  if (data.advisor_arch_goal) {
    const aa_kind = data.advisor_arch_goal.kind;
    if (
      aa_kind === "GROWTH_X_CAPITAL_PRESERVATION" ||
      aa_kind === "Risk Based Framework"
    ) {
      portfolios = {
        Growth: data.advisor_arch_goal.risk_score,
        "Capital Preservation": 100 - data.advisor_arch_goal.risk_score,
      };
    } else if (aa_kind === "MONEY_MARKET" || aa_kind === "Short term T-Bills") {
      portfolios = {
        "Money Market": 100,
      };
    } else {
      portfolios = {
        Yield: 100,
      };
    }
  }
  const account: AccountInfo = {
    goal: data.goal,
    account_detail: data.account_detail,
    cash_available_for_withdraw: data.cash_available_for_withdraw,
    open_balance: data.open_balance,
    total_equity: data.total_equity,
    buying_power: data.buying_power,
    ira_contribution_constraints:
      data.ira_contribution_constraints ?? undefined,
    ira_distribution_constraints:
      data.ira_distribution_constraints ?? undefined,
    deposits: data.deposits,
    withdrawals: data.withdrawals,
    holdings: data.holdings,
    portfolios: portfolios,
    historical_positions: data.historical_positions,
  };
  return account;
}

export type HoldingPositionNumerical = Omit<HoldingPosition, "amount"> & {
  amount: number;
  unit: "USD" | "Unit";
};

export function makeHoldingsNumerical(
  holding: HoldingPosition,
): HoldingPositionNumerical {
  let amount = parseFloat(holding.amount);
  let unit: "USD" | "Unit" = "USD";
  if (holding.asset_type != "CURRENCY" && holding.stock_unit_price) {
    amount = parseFloat(holding.stock_unit_price) * amount;
  }
  if (holding.asset_type != "CURRENCY" && !holding.stock_unit_price) {
    unit = "Unit";
  }
  return {
    ...holding,
    amount: amount,
    unit: unit,
  };
}

export function mergeHoldings(
  holdings: Array<HoldingPositionNumerical>,
): Array<HoldingPositionNumerical> {
  return holdings
    .sort((a, b) => (a.asset_id < b.asset_id ? 1 : -1))
    .reduce((accu, asset) => {
      if (accu.length === 0) {
        return [asset];
      }
      const [head, ...rest] = accu;
      if (asset.asset_id === head.asset_id) {
        head.amount += asset.amount;
        return [head, ...rest];
      }
      return [asset, head, ...rest];
    }, [] as HoldingPositionNumerical[]);
}

/**
 * get holdings data
 * @param accounts accounts list
 * @param accountId optional account id
 * @param assetMap asset map
 * @param portfolioName optional portfolio name
 * @returns processed holdings data
 */
export function getHoldings(
  accounts: AccountInfo[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
  portfolioName: string | undefined,
): HoldingPositionNumerical[] {
  // get relevant assets set
  const relevantAssets = portfolioName
    ? new Set(assetMap?.portfolio_map[portfolioName])
    : undefined;

  // filter asset function
  function filterByAssetMap(h: HoldingPosition) {
    if (portfolioName === undefined) {
      return true;
    }
    if (relevantAssets?.has(h.symbol)) {
      return true;
    }
    return false;
  }

  // get all holdings
  const allHoldings = accounts
    .filter(
      (a) =>
        accountId === undefined || a.account_detail.account_id === accountId,
    )
    .map((a) => a.holdings ?? [])
    .flat()
    .filter(filterByAssetMap)
    .map(makeHoldingsNumerical);

  // if no account id is specified, merge holdings
  if (accountId === undefined) {
    return mergeHoldings(allHoldings);
  }

  return allHoldings;
}

export type Node = {
  id: number;
  text: string;
  valueUsd: number;
  valuePercent: number;
  type: "Security" | "Class";
  children: Array<Node>;
  isLeaf: boolean;
};

export function buildTree(
  holdings: Array<HoldingPositionNumerical>,
  classes: Array<AdvisorArchAssetClass>,
): Node {
  const total = sum(holdings, (h) => h.amount);
  const moneyMap = new Map();
  for (const h of holdings) {
    moneyMap.set(h.symbol, {
      amount: h.amount,
      percentage: h.amount / total,
    });
    if (isCashSweepSymbol(h.symbol)) {
      // WORKAROUND: AdvisorArch's class tree values cash under the CASH_USD
      // symbol, while Apex reports cash-sweep vehicles (USD, FQLPZ, …) under
      // their own symbols. Remap them so the tree's cash node picks them up.
      moneyMap.set("CASH_USD", {
        amount: h.amount,
        percentage: h.amount / total,
      });
    }
  }

  const processNode = (c: AdvisorArchAssetClass): Node => {
    const children = c.children.map(processNode);
    for (const security of c.securities) {
      children.push({
        id: security.id,
        type: "Security",
        text: security.short_name,
        valueUsd: moneyMap.get(security.symbol)?.amount ?? 0,
        valuePercent: moneyMap.get(security.symbol)?.percentage ?? 0,
        children: [],
        isLeaf: true,
      });
    }
    const valueUsd = sum(children, (c) => c.valueUsd);
    const valuePercent = sum(children, (c) => c.valuePercent);
    return {
      id: c.id,
      type: "Class",
      text: c.description,
      valueUsd,
      valuePercent,
      children,
      isLeaf: c.children.length === 0,
    };
  };

  const nodes = classes.map(processNode);
  const root: Node = {
    id: -1,
    text: "__ROOT",
    valueUsd: sum(nodes, (n) => n.valueUsd),
    valuePercent: sum(nodes, (n) => n.valuePercent),
    type: "Class",
    children: nodes,
    isLeaf: false,
  };
  return root;
}

export type AssetClassesViewProps = {
  accountId?: string;
  portfolioName?: string;
};

export function getAssetClassesView(
  accounts: AccountInfo[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
) {
  const holdings = getHoldings(accounts, accountId, assetMap, undefined);
  const classes = assetMap?.class_tree ?? [];
  return buildTree(holdings, classes);
}

export function getHoldingsFromService(
  apexAccounts: ApexAccount[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
  portfolioName: string | undefined,
): HoldingPositionNumerical[] {
  // get relevant assets set
  const relevantAssets = portfolioName
    ? new Set(assetMap?.portfolio_map[portfolioName])
    : undefined;

  // filter asset function
  function filterByAssetMap(h: HoldingPosition) {
    if (portfolioName === undefined) {
      return true;
    }
    if (relevantAssets?.has(h.symbol)) {
      return true;
    }
    return false;
  }

  // get all holdings
  const allHoldings = apexAccounts
    .filter((a) => accountId === undefined || a.id === accountId)
    .map((a) => a.holdings ?? [])
    .flat()
    .filter(filterByAssetMap)
    .map(makeHoldingsNumerical);

  // if no account id is specified, merge holdings
  if (accountId === undefined) {
    return mergeHoldings(allHoldings);
  }

  return allHoldings;
}

export const generateAssetClassesView = (
  apexAccounts: ApexAccount[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
) => {
  const holdings = getHoldingsFromService(
    apexAccounts,
    accountId,
    assetMap,
    undefined,
  );

  const classes = assetMap?.class_tree ?? [];
  return buildTree(holdings, classes);
};

// Alias an Apex/AdvisorArch class-tree label ("Equity") to the frontend
// allocation category ("Equities") before matching.
const APEX_ASSET_CLASS_ALIASES: Record<string, string> = {
  Equity: "Equities",
};

export const normalizeApexAssetClass = (label: string): string =>
  APEX_ASSET_CLASS_ALIASES[label] ?? label;

// Map an Apex holding's asset_type to a top-level allocation category. Used as a
// fallback for holdings whose symbol is absent from the asset-map class_tree.
// Note: Apex asset_type has no commodity value (a gold ETF such as IAU reports
// EQUITY), so this fallback cannot resolve "Commodities" — those are only
// identifiable via the class_tree, which the primary tree path already handles.
export const classifyApexAssetType = (assetType?: string): string => {
  switch (assetType) {
    case "EQUITY":
    case "MUTUAL_FUND":
    case "DIGITAL":
      return "Equities";
    case "FIXED_INCOME":
      return "Fixed Income";
    case "CURRENCY":
    case "FDIC_SYNTHETIC":
      return "Cash";
    default:
      return "Other";
  }
};

// Allocation for Apex holdings whose symbol is not present in the class_tree.
// buildTree only assigns value to securities defined in the class_tree, so any
// held symbol outside the model portfolio (e.g. ROCQ, ARCI) is otherwise dropped
// entirely. These positions are bucketed by their authoritative asset_type using
// value_usd from the holdings API.
export function getUnclassifiedApexAllocation(
  apexAccounts: ApexAccount[],
  assetMap: AssetMap | undefined,
): Record<string, number> {
  const allocation: Record<string, number> = {
    Equities: 0,
    Commodities: 0,
    "Fixed Income": 0,
    Cash: 0,
    Other: 0,
  };

  const classTreeSymbols = new Set<string>();
  const collect = (nodes: AdvisorArchAssetClass[]) => {
    for (const node of nodes) {
      for (const security of node.securities ?? []) {
        classTreeSymbols.add(security.symbol);
      }
      collect(node.children ?? []);
    }
  };
  collect(assetMap?.class_tree ?? []);

  // buildTree remaps cash-sweep symbols (USD, FQLPZ, …) to CASH_USD. When the
  // class tree defines a CASH_USD security the tree already values them, so we
  // skip them here to avoid double-counting; when it does not (e.g. an empty
  // class tree) they would otherwise be dropped, so we bucket them as cash.
  const treeValuesCash = classTreeSymbols.has("CASH_USD");

  for (const account of apexAccounts) {
    for (const holding of account.holdings ?? []) {
      // Any symbol present in the class tree is already valued there by
      // buildTree (via moneyMap.get(symbol)) — skip to avoid double-counting.
      if (classTreeSymbols.has(holding.symbol)) continue;

      // Cash sweeps are additionally remapped to CASH_USD by buildTree; if the
      // tree defines CASH_USD they're counted there too, otherwise they'd be
      // dropped, so bucket them as cash here.
      const isCashSweep = isCashSweepSymbol(holding.symbol);
      if (isCashSweep && treeValuesCash) continue;

      const raw = (holding as unknown as { value_usd?: string | number })
        .value_usd;
      let value = typeof raw === "number" ? raw : parseFloat(raw ?? "");
      if (!Number.isFinite(value) || value === 0) {
        // Fall back to shares * unit price when value_usd is absent.
        const shares = parseFloat(holding.amount ?? "");
        const price =
          isCashSweep || holding.asset_type === "CURRENCY"
            ? parseFloat(holding.stock_unit_price ?? "") || 1
            : parseFloat(holding.stock_unit_price ?? "");
        value = shares * price;
      }
      if (!Number.isFinite(value) || value === 0) continue;

      allocation[
        isCashSweep ? "Cash" : classifyApexAssetType(holding.asset_type)
      ] += value;
    }
  }

  return allocation;
}

export type MergedHistoricalPosition = {
  date: string;
  quantity: number;
  symbol: string;
  value_usd: number;
};

export function buildMergedHistoricalPosition(accounts: AccountInfo[]) {
  const historicalPositionMap: Record<
    string,
    Record<string, MergedHistoricalPosition>
  > = {};
  const dates = new Set<string>();
  for (const a of accounts) {
    for (const pos of a.historical_positions ?? []) {
      dates.add(pos.date);
      if (!(pos.date in historicalPositionMap)) {
        historicalPositionMap[pos.date] = {};
      }
      if (!(pos.symbol in historicalPositionMap[pos.date])) {
        historicalPositionMap[pos.date][pos.symbol] = {
          date: pos.date,
          symbol: pos.symbol,
          quantity: 0,
          value_usd: 0,
        };
      }
      historicalPositionMap[pos.date][pos.symbol].quantity += parseFloat(
        pos.quantity,
      );
      historicalPositionMap[pos.date][pos.symbol].value_usd += parseFloat(
        pos.value_usd,
      );
    }
  }
  const mergedHistoricalPositions: MergedHistoricalPosition[] = [];
  for (const date of dates.values()) {
    for (const symbol in historicalPositionMap[date]) {
      mergedHistoricalPositions.push(historicalPositionMap[date][symbol]);
    }
  }
  return mergedHistoricalPositions;
}

export function isClosedOrPrepareForClosure(account: ApexAccount) {
  return isClosedOrClosingAccount(account.account_detail);
}
